import { LancerCounter } from "@thilke/ui/components/lancer-counter";
import { cn } from "@thilke/ui/lib/utils";
import type * as React from "react";

export interface LancerStructureStressProps
  extends Omit<React.ComponentProps<"div">, "children"> {
  type: "structure" | "stress";
  current: number;
  max: number;
  name?: string;
  damage?: number;
  rollDice?: number;
  onPipClick?: (index: number) => void;
}

function LancerStructureStress({
  type,
  current,
  max,
  name,
  damage,
  rollDice,
  onPipClick,
  className,
  ...props
}: LancerStructureStressProps) {
  const label = type === "structure" ? "STRUCTURE" : "STRESS";
  const tone = type === "structure" ? "default" : "warning";
  const displayDamage = damage ?? Math.max(0, max - current);
  const displayRollDice = rollDice ?? displayDamage;

  return (
    <div
      data-slot="lancer-structure-stress"
      data-type={type}
      className={cn(
        "grid gap-2 rounded-[4px] border border-border/70 bg-card p-3 shadow-sm",
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "grid size-8 place-items-center rounded-[3px] border font-mono text-xs font-bold uppercase",
            type === "structure"
              ? "border-primary/40 bg-primary/10 text-primary"
              : "border-warning/50 bg-warning/10 text-warning",
          )}
        >
          {type === "structure" ? "S" : "R"}
        </div>
        <div className="min-w-0">
          <div className="font-mono text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </div>
          {name ? (
            <div className="truncate text-xs font-medium text-foreground">
              {name}
            </div>
          ) : null}
        </div>
        <div className="ml-auto font-mono text-xs font-semibold text-foreground">
          {current}/{max}
        </div>
      </div>

      <LancerCounter
        value={current}
        max={max}
        tone={tone}
        size="lg"
        showValue={false}
        interactive={!!onPipClick}
        onPipClick={onPipClick}
        className="justify-center"
      />

      {displayDamage > 0 ? (
        <div className="flex items-center justify-between rounded-[3px] border border-border/60 bg-muted/20 px-2 py-1.5">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Damage preview
          </span>
          <span className="font-mono text-xs font-semibold text-destructive">
            {displayDamage}d6
          </span>
        </div>
      ) : (
        <div className="flex items-center justify-between rounded-[3px] border border-border/60 bg-muted/20 px-2 py-1.5">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Status
          </span>
          <span className="font-mono text-xs font-semibold text-success">
            Stable
          </span>
        </div>
      )}

      {displayRollDice > 0 ? (
        <p className="text-center text-xs text-muted-foreground">
          Roll{" "}
          <span className="font-mono font-semibold">{displayRollDice}d6</span>{" "}
          to determine outcome.
        </p>
      ) : null}
    </div>
  );
}

export { LancerStructureStress };
